import { BaseService } from './BaseService'
import type { PlaybackTarget } from './broadcastService'

/**
 * Broadcast analysis support tooling.
 *
 * Two distinct scopes, kept apart deliberately:
 *   - entitlement* : per customer, every call carries customerId
 *   - reference*   : PLATFORM-WIDE, no customerId anywhere
 *
 * If a reference call ever grows a customerId, that is the bug: sports,
 * leagues, teams and venues are global, and someone would eventually
 * deactivate a league believing they were configuring one customer.
 */

export interface CreditBalance {
  purchased: number
  adjusted: number
  consumed: number
  remaining: number
}

export interface BroadcastAllowance {
  id: string
  period_start: string
  period_end: string
  period_label: string | null
  credits_purchased: number
  hours_per_credit: number
  notes: string | null
  created_at?: string
  updated_at?: string
}

export interface BroadcastEntitlement {
  hasAllowance: boolean
  allowance?: {
    id: string
    periodStart: string
    periodEnd: string
    periodLabel: string | null
    hoursPerCredit: number
    notes: string | null
  }
  balance: CreditBalance
  exhausted?: boolean
  warning?: boolean
  message?: string
}

export type AdjustmentReason =
  | 'degraded_content' | 'failed_analysis' | 'goodwill' | 'correction' | 'promotional'

export interface BroadcastAdjustment {
  id: string
  credits: number
  reason_code: AdjustmentReason
  notes: string
  granted_by: string
  created_at: string
  broadcast_id: string | null
  broadcast_title: string | null
}

export interface RefSport   { id: string; code: string; name: string; display_order: number; is_active: boolean }
export interface RefLeague  { id: string; code: string; name: string; short_name: string | null; kind: string
                              sport_id: string | null; sport_code?: string | null; sport_name?: string | null
                              country: string | null
                              season_format: 'calendar' | 'split'
                              display_order: number; is_active: boolean }
export interface RefVenue   { id: string; name: string; city: string | null; region: string | null
                              country: string | null; timezone: string | null; is_active: boolean }
export interface RefTeam    { id: string; league_id: string; sport_id: string | null; name: string
                              short_name: string | null; abbreviation: string | null
                              home_venue_id: string | null; home_venue_name?: string | null
                              league_code?: string; league_short_name?: string | null
                              sport_code?: string | null; is_active: boolean }
export interface RefViewershipSource { id: string; code: string; name: string; is_estimate: boolean
                                       display_order: number; is_active: boolean }

export interface BroadcastChannel {
  id: string; code: string; name: string
  channel_type: 'linear' | 'svod' | 'fast' | 'free_stream' | 'social' | 'other'
  territory: string | null
}

/** `mine` is what the customer broadcasts on; `all` is the catalogue behind
 *  "add another". Showing only `all` is the mile-long list this avoids. */
export interface ChannelOptions { mine: BroadcastChannel[]; all: BroadcastChannel[] }

export type ReferenceType =
  | 'sports' | 'leagues' | 'teams' | 'venues' | 'viewership-sources' | 'competition-stages'

export interface RefCompetitionStage {
  id: string; code: string; name: string; display_order: number; is_active: boolean
}

export type BroadcastStatus =
  | 'uploaded' | 'queued' | 'estimating' | 'awaiting_approval' | 'sampling'
  | 'detecting' | 'aggregating' | 'capturing' | 'review' | 'published'
  | 'failed' | 'halted' | 'archived'

export interface Broadcast {
  id: string
  title: string
  season: string | null
  competition_stage_name: string | null
  competition_stage_code: string | null
  event_date: string | null
  status: BroadcastStatus
  duration_ms: number | null
  file_size_bytes: number | null
  width: number | null
  height: number | null
  credits_charged: number | null
  credits_charged_at: string | null
  parent_broadcast_id: string | null
  error_message: string | null
  created_at: string
  updated_at: string
  sport_name: string | null
  league_short_name: string | null
  venue_name: string | null
  home_team_name: string | null
  away_team_name: string | null
  child_count: number
  children?: Broadcast[]
  /**
   * Measurement and commercial inputs, edited by support after the fact.
   * Audience lands days or weeks after an event, so these are normally NULL
   * on a broadcast that has already been analysed — not an error state.
   */
  viewers: number | null
  cpm: number | null
  currency: string | null
  viewership_source_id: string | null
  channel_id: string | null
  channel_name: string | null
  feed_role: string | null
  /**
   * Whether Instant Replay exists. False on anything uploaded before the
   * remux shipped, and on a remux that failed — both only fixable by
   * someone building it, which is why support can see this at all.
   */
  has_playback?: boolean

  /**
   * Resolved server-side from the customer's rate cards, so every surface
   * agrees to the cent. `cpm` above is the explicit OVERRIDE and is usually
   * null; `effective_cpm` is what actually prices this broadcast.
   */
  rate_card_id: string | null
  rate_card_name: string | null
  rate_card_cpm: number | null
  effective_cpm: number | null
  effective_currency: string | null
  /** 'negotiated_cpm' | 'rate_card', or null when nothing prices it. */
  valuation_basis: string | null
  /** audience / 1000 x effective_cpm. Null until both are known. */
  rate_per_30s: number | null
}

export interface BroadcastPreflight {
  ok: boolean
  reason?: string
  durationMs: number
  width: number
  height: number
  frames: number
  estimatedCostUsd: number
  creditsRequired: number
  creditsRemaining: number | null
  status?: string
}

export type ExclusionReason =
  | 'not_a_brand' | 'own_mark' | 'crowd' | 'misread' | 'duplicate' | 'other'

export type MarkType = 'sponsor' | 'competition' | 'team' | 'venue' | 'broadcaster' | 'unknown'
export type MarkSource = 'reference' | 'model' | 'reviewer'

export interface ReviewBrand {
  /** Normalised form. Rows are grouped on THIS, so it is the stable identity;
   *  brand_name is only the commonest spelling of the group. */
  brand_key: string
  /** Keys folded into this row by a merge. Empty when nothing is merged.
   *  Carried so un-merge is reachable: the alias itself no longer has a row. */
  merged_keys: string[]
  /** What kind of entity this mark belongs to. Null = not classified. */
  mark_type: MarkType | null
  mark_source: MarkSource | null
  /** Matched this customer's partner or hint list, so it counts whatever its type. */
  is_promoted: boolean
  /**
   * THE PRIMARY CLASSIFICATION. True when the brand resolves to an ACTIVE
   * partner by name or alias — inventory somebody paid for. Everything else
   * is a mark, including hinted brands, which count but name no sponsor.
   *
   * Resolved from the partner list on every read, so it changes the moment a
   * partner is added, aliased, deleted or restored. Nothing is stored against
   * the broadcast, and nothing in the UI may edit it.
   */
  is_sponsored: boolean
  partner_id: string | null
  partner_name: string | null
  /** Occurrences a reviewer has excluded; they contribute to no total. */
  excluded_appearances: number
  /** The server's verdict: does this brand count toward reported sponsor value. */
  counts: boolean
  brand_name: string
  /**
   * What to SHOW. The partner's own name when the brand resolves to one,
   * then a curator's merge name, then the longest detected spelling.
   *
   * brand_name stays the identifier — the exclusion and appearance endpoints
   * take it in the path — so the two must not be swapped. Display this,
   * send that.
   */
  display_name: string
  is_own: boolean
  excluded_reason: ExclusionReason | null
  appearances: number
  total_duration_ms: number
  total_area_seconds: number
  avg_confidence: number
  zones: string
  raw_names: string[] | null
  max_area_share: number | null
  /** the appearance where this brand filled the most screen — the thumbnail source */
  peak_appearance_id: string | null
  peak_frame_ms: number | null
}

export interface RefZone {
  code: string
  label: string
  description: string | null
  display_order: number
}

export interface BrandAppearance {
  id: string
  zone_code: string
  /** the model's read, present only once a reviewer has corrected it */
  zone_code_original: string | null
  zone_corrected_at: string | null
  start_time_ms: number
  end_time_ms: number
  duration_ms: number
  frame_count: number
  peak_frame_ms: number | null
  frame_cached: boolean
  avg_confidence: number | null
  avg_area_share: number | null
  max_area_share: number | null
  /** Set when a reviewer excluded THIS occurrence, not the whole brand. */
  excluded_reason: ExclusionReason | null
  area_seconds: number | null
}

export interface ReviewCoverage {
  zone_code: string
  /** null when the code is no longer in the zone enum — show the code alone. */
  zone_label: string | null
  named: number
  unreadable: number
  pct_unreadable: number
}

export interface BroadcastReview {
  broadcast: Broadcast & {
    estimated_cost_usd: number | null
    actual_cost_usd: number | null
    prompt_version: string | null
    detection_model: string | null
    analysis_started_at: string | null
    analysis_ended_at: string | null
    reviewed_at: string | null
    /** Resolved names for the valuation popup — a uuid proves nothing. */
    viewership_source_name?: string | null
    viewership_is_estimate?: boolean | null
  /** The uploaded file's own name, without the storage path or its uuid. */
  source_filename?: string | null
    review_note: string | null
    /** what the run actually did; empty on broadcasts analysed before this was recorded */
    metadata: {
      frames_analysed?: number
      frames_with_detections?: number
      sample_interval_ms?: number
      min_confidence?: number
      parse_failures?: number
    } | null
  }
  brands: ReviewBrand[]
  coverage: ReviewCoverage[]
  scenes: { scene: string; frames: number }[]
  /**
   * What the model looked at, from broadcast_frames. NULL for a broadcast
   * analysed before that table existed — absent means NOT MEASURED and must
   * never render as zero.
   */
  frames: {
    analysed: number
    withDetections: number
    /** Of those, how many carried at least one sponsored brand. */
    withSponsored: number
  } | null
}

export interface UploadTicket {
  uploadUrl: string
  blobPath: string
  expiresOn: string
  maxFileBytes: number
}

interface Wrapped<T> { success: boolean; data: T; message?: string }


/**
 * A customer's CPM, effective-dated. Never edited in place: repricing is
 * close-the-old, open-the-new, so a figure shown to a sponsor last month
 * still resolves to that figure today.
 */
export interface RateCard {
  id: string
  name: string
  cpm: number
  currency: string
  /** The carrier this rate is for. Descriptive — nothing matches on it. */
  channel_id: string | null
  channel_name: string | null
  channel_territory: string | null
  effective_from: string
  /** When the rate stopped applying. Does NOT hide the card. */
  effective_to: string | null
  /** effective_to is null — the rate is still current. */
  is_current: boolean
  /** False = not offered in the picker. Broadcasts already assigned keep their price. */
  is_enabled: boolean
  notes: string | null
  /** Broadcasts assigned to this card. Blocks deletion. */
  broadcasts_using: number
  created_at: string
  updated_at: string
}

export interface CreateRateCardRequest {
  name: string
  cpm: number
  currency?: string
  channelId?: string | null
  effectiveFrom: string
  effectiveTo?: string | null
  notes?: string | null
}

/** One field of one broadcast's valuation, changed once. */
export interface ValuationLogEntry {
  id: string
  field: string
  old_value: string | null
  new_value: string | null
  /** What an id meant at the time — cards get renamed. */
  old_label: string | null
  new_label: string | null
  note: string | null
  created_at: string
  actor_user_id: string | null
  actor_name: string | null
}

export interface ValuationBasis {
  code: string
  name: string
  description: string | null
  is_estimate: boolean
}

class BroadcastAdminService extends BaseService {
  constructor() {
    super('cro')
  }

  // ── Playback ───────────────────────────────────────────────────────────────

  /**
   * A short-lived signed URL for the seekable copy. Support sees any status,
   * because reviewing happens long before publishing.
   *
   * Throws with status 409 while the copy is still being remuxed, which the
   * player renders as "preparing" rather than as a failure.
   */
  async getPlaybackUrl(customerId: string, id: string): Promise<Wrapped<PlaybackTarget>> {
    return this.get<Wrapped<PlaybackTarget>>(
      `/broadcast/admin/broadcasts/${id}/playback-url`, { customerId }
    )
  }

  /** Rebuild the playback copy — for a remux that failed or used an old recipe. */
  async preparePlayback(id: string, force = false): Promise<Wrapped<unknown>> {
    return this.post<Wrapped<unknown>>(`/broadcast/admin/broadcasts/${id}/prepare-playback`, { force }, {})
  }

  // ── Entitlement (per customer) ─────────────────────────────────────────────

  async getEntitlement(customerId: string): Promise<Wrapped<BroadcastEntitlement>> {
    return this.get<Wrapped<BroadcastEntitlement>>('/broadcast/entitlement', { customerId })
  }

  async listAllowances(customerId: string): Promise<Wrapped<BroadcastAllowance[]>> {
    return this.get<Wrapped<BroadcastAllowance[]>>('/broadcast/entitlement/allowances', { customerId })
  }

  async createAllowance(
    customerId: string,
    payload: {
      periodStart: string; periodEnd: string; periodLabel?: string | null
      creditsPurchased: number; hoursPerCredit?: number; notes?: string | null
    }
  ): Promise<Wrapped<BroadcastAllowance>> {
    return this.post<Wrapped<BroadcastAllowance>>('/broadcast/entitlement/allowances', payload, { customerId })
  }

  async updateAllowance(
    customerId: string, id: string, updates: Partial<BroadcastAllowance> & Record<string, unknown>
  ): Promise<Wrapped<BroadcastAllowance>> {
    return this.put<Wrapped<BroadcastAllowance>>(`/broadcast/entitlement/allowances/${id}`, updates, { customerId })
  }

  async listAdjustments(customerId: string): Promise<Wrapped<BroadcastAdjustment[]>> {
    return this.get<Wrapped<BroadcastAdjustment[]>>('/broadcast/entitlement/adjustments', { customerId })
  }

  /** Grant or correct credits. `notes` is required — an adjustment without a reason is unauditable. */
  async createAdjustment(
    customerId: string,
    payload: {
      credits: number; reasonCode: AdjustmentReason; notes: string
      broadcastId?: string | null; allowanceId?: string | null
    }
  ): Promise<Wrapped<BroadcastAdjustment>> {
    return this.post<Wrapped<BroadcastAdjustment>>('/broadcast/entitlement/adjustments', payload, { customerId })
  }

  // ── Broadcasts (support) ───────────────────────────────────────────────────

  async getUploadUrl(
    customerId: string, payload: { fileName: string; fileSizeBytes?: number | null }
  ): Promise<Wrapped<UploadTicket>> {
    return this.post<Wrapped<UploadTicket>>('/broadcast/admin/broadcasts/upload-url', payload, { customerId })
  }

  /** Estimate + entitlement check. Spends nothing, writes nothing. */
  async preflightBroadcast(customerId: string, id: string): Promise<Wrapped<BroadcastPreflight>> {
    return this.get<Wrapped<BroadcastPreflight>>(`/broadcast/admin/broadcasts/${id}/preflight`, { customerId })
  }

  /** Debits credits and starts the run. Returns immediately — poll for status. */
  async analyzeBroadcast(
    customerId: string, id: string, force = false
  ): Promise<Wrapped<BroadcastPreflight>> {
    return this.post<Wrapped<BroadcastPreflight>>(
      `/broadcast/admin/broadcasts/${id}/analyze`, { force }, { customerId }
    )
  }

  async listBroadcasts(customerId: string): Promise<Wrapped<Broadcast[]>> {
    return this.get<Wrapped<Broadcast[]>>('/broadcast/admin/broadcasts', { customerId })
  }

  async getBroadcast(customerId: string, id: string): Promise<Wrapped<Broadcast>> {
    return this.get<Wrapped<Broadcast>>(`/broadcast/admin/broadcasts/${id}`, { customerId })
  }

  async createBroadcast(
    customerId: string, payload: Record<string, unknown>
  ): Promise<Wrapped<Broadcast>> {
    return this.post<Wrapped<Broadcast>>('/broadcast/admin/broadcasts', payload, { customerId })
  }

  async updateBroadcast(
    customerId: string, id: string, updates: Record<string, unknown>
  ): Promise<Wrapped<Broadcast>> {
    return this.put<Wrapped<Broadcast>>(`/broadcast/admin/broadcasts/${id}`, updates, { customerId })
  }

  /**
   * PUTs the file straight to blob storage with the minted SAS. The API never
   * sees the bytes — a 4-5 GB body would blow App Service's limits and a
   * failure at 90% would mean starting again.
   *
   * Block upload: 8 MB blocks, committed at the end. onProgress reports 0-1.
   */
  async uploadToBlob(
    uploadUrl: string,
    file: File,
    onProgress?: (fraction: number) => void,
    signal?: AbortSignal
  ): Promise<void> {
    const BLOCK = 8 * 1024 * 1024
    const blockIds: string[] = []
    let sent = 0

    for (let offset = 0, i = 0; offset < file.size; offset += BLOCK, i++) {
      if (signal?.aborted) throw new DOMException('Cancelled by user', 'AbortError')
      const chunk = file.slice(offset, Math.min(offset + BLOCK, file.size))
      // block ids must be equal-length and base64 across the whole blob
      const blockId = btoa(String(i).padStart(6, '0'))
      blockIds.push(blockId)

      const res = await fetch(
        `${uploadUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`,
        { method: 'PUT', body: chunk, signal }
      )
      if (!res.ok) throw new Error(`Block upload failed (${res.status}) at ${(offset / 1e9).toFixed(1)} GB`)

      sent += chunk.size
      onProgress?.(sent / file.size)
    }

    const list =
      `<?xml version="1.0" encoding="utf-8"?><BlockList>` +
      blockIds.map((b) => `<Latest>${b}</Latest>`).join('') +
      `</BlockList>`

    const commit = await fetch(`${uploadUrl}&comp=blocklist`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/xml' },
      body: list,
      signal
    })
    if (!commit.ok) throw new Error(`Could not finalise upload (${commit.status})`)
    onProgress?.(1)
  }

  // ── Review ─────────────────────────────────────────────────────────────────

  async getBrandAppearances(
    customerId: string, id: string, brandName: string
  ): Promise<Wrapped<BrandAppearance[]>> {
    return this.get<Wrapped<BrandAppearance[]>>(
      `/broadcast/admin/broadcasts/${id}/review/brands/${encodeURIComponent(brandName)}/appearances`,
      { customerId }
    )
  }

  /**
   * Src for an <img>. The first request for a given appearance makes the
   * backend seek the video and store the frame; later ones are a blob read.
   */
  // ── Rate cards ────────────────────────────────────────────────────────────

  /**
   * `enabledOnly` is what the PICKER asks for. The management screen wants
   * everything, disabled cards included — hiding them is how three people
   * end up recreating a card nobody can see.
   */
  async listRateCards(customerId: string, enabledOnly = false): Promise<Wrapped<RateCard[]>> {
    return this.get<Wrapped<RateCard[]>>('/broadcast/rate-cards',
      enabledOnly ? { customerId, enabledOnly: 'true' } : { customerId })
  }

  /** Nothing is auto-closed: any number of cards can be current at once. */
  async createRateCard(
    customerId: string, data: CreateRateCardRequest
  ): Promise<Wrapped<RateCard>> {
    return this.post<Wrapped<RateCard>>('/broadcast/rate-cards', data, { customerId })
  }

  /**
   * Name, notes and whether it is offered. The rate itself is immutable: a
   * broadcast stores rate_card_id, so the card's cpm IS its price.
   */
  async updateRateCard(
    customerId: string, id: string,
    data: { name?: string; notes?: string | null; isEnabled?: boolean }
  ): Promise<Wrapped<RateCard>> {
    return this.patch<Wrapped<RateCard>>(`/broadcast/rate-cards/${id}`, data, { customerId })
  }

  /**
   * Records when the rate stopped applying. Does NOT hide the card and
   * changes no broadcast — a closed card is still the right one to assign to
   * a game that aired while it was in force.
   */
  async closeRateCard(
    customerId: string, id: string, effectiveTo: string
  ): Promise<Wrapped<RateCard>> {
    return this.post<Wrapped<RateCard>>(
      `/broadcast/rate-cards/${id}/close`, { effectiveTo }, { customerId })
  }

  async deleteRateCard(customerId: string, id: string): Promise<Wrapped<unknown>> {
    return this.delete(`/broadcast/rate-cards/${id}`, { customerId })
  }

  async listValuationBases(): Promise<Wrapped<ValuationBasis[]>> {
    return this.get<Wrapped<ValuationBasis[]>>('/broadcast/valuation-bases')
  }

  /** The trail behind one broadcast's money figure, newest first. */
  async getValuationLog(
    customerId: string, broadcastId: string
  ): Promise<Wrapped<ValuationLogEntry[]>> {
    return this.get<Wrapped<ValuationLogEntry[]>>(
      `/broadcast/admin/broadcasts/${broadcastId}/valuation-log`, { customerId })
  }

  async listChannels(customerId: string): Promise<Wrapped<ChannelOptions>> {
    return this.get<Wrapped<ChannelOptions>>('/broadcast/admin/channels', { customerId })
  }

  async listZones(broadcastId?: string): Promise<Wrapped<RefZone[]>> {
    // Scoped to the broadcast's sport: a reviewer correcting a
    // basketball zone must not be offered `dugout`, and the model was
    // never shown it either.
    return this.get<Wrapped<RefZone[]>>('/broadcast/reference/zones',
      broadcastId ? { broadcastId } : undefined)
  }

  /** Correct where a brand physically sat. Keeps the model's original read. */
  async setAppearanceZone(
    customerId: string, id: string, appearanceId: string, zoneCode: string
  ): Promise<Wrapped<{ id: string; zone_code: string; zone_code_original: string | null }>> {
    return this.put(
      `/broadcast/admin/broadcasts/${id}/review/appearances/${appearanceId}/zone`,
      { zoneCode }, { customerId }
    )
  }

  getFrameUrl(customerId: string, id: string, appearanceId: string): string {
    return `${this.baseURL}/broadcast/admin/broadcasts/${id}/frame`
      + `?customerId=${customerId}&appearanceId=${appearanceId}`
  }

  async getReview(customerId: string, id: string): Promise<Wrapped<BroadcastReview>> {
    return this.get<Wrapped<BroadcastReview>>(`/broadcast/admin/broadcasts/${id}/review`, { customerId })
  }

  /** null restores the brand to the customer-facing numbers. */
  async setBrandExclusion(
    customerId: string, id: string, brandName: string, excludedReason: ExclusionReason | null
  ): Promise<Wrapped<unknown>> {
    return this.put<Wrapped<unknown>>(
      `/broadcast/admin/broadcasts/${id}/review/brands/${encodeURIComponent(brandName)}`,
      { excludedReason }, { customerId }
    )
  }

  /** One occurrence, not the whole brand. */
  async setAppearanceExclusion(
    customerId: string, id: string, appearanceId: string, excludedReason: ExclusionReason | null
  ): Promise<Wrapped<unknown>> {
    return this.put(
      `/broadcast/admin/broadcasts/${id}/review/appearances/${appearanceId}/exclusion`,
      { excludedReason }, { customerId }
    )
  }

  /** "Finished with, not for the customer." Never refunds credits. */
  async archiveBroadcast(customerId: string, id: string, note?: string): Promise<Wrapped<unknown>> {
    return this.post(`/broadcast/admin/broadcasts/${id}/archive`, { note: note ?? null }, { customerId })
  }

  /** Returns to review, never straight back to published. */
  async restoreBroadcast(customerId: string, id: string): Promise<Wrapped<unknown>> {
    return this.post(`/broadcast/admin/broadcasts/${id}/restore`, {}, { customerId })
  }

  async mergeBrand(
    customerId: string, aliasKey: string, intoKey: string, canonicalName?: string, note?: string
  ): Promise<Wrapped<unknown>> {
    return this.post('/broadcast/merges',
      { aliasKey, intoKey, canonicalName: canonicalName ?? null, note: note ?? null },
      { customerId })
  }

  async unmergeBrand(customerId: string, aliasKey: string): Promise<Wrapped<unknown>> {
    return this.delete(`/broadcast/merges/${encodeURIComponent(aliasKey)}`, { customerId })
  }

  async setBrandMark(
    customerId: string, id: string, brandName: string, markType: MarkType | null
  ): Promise<Wrapped<unknown>> {
    return this.put(
      `/broadcast/admin/broadcasts/${id}/review/brands/${encodeURIComponent(brandName)}/mark`,
      { markType }, { customerId }
    )
  }

  /** Free: no frames sampled, model never called. */
  async reclassifyBroadcast(
    customerId: string, id: string
  ): Promise<Wrapped<{ classified: number; byType: Record<string, number> }>> {
    return this.post(`/broadcast/admin/broadcasts/${id}/reclassify`, {}, { customerId })
  }

  async publishBroadcast(customerId: string, id: string, note?: string): Promise<Wrapped<unknown>> {
    return this.post<Wrapped<unknown>>(
      `/broadcast/admin/broadcasts/${id}/review/publish`, { note }, { customerId }
    )
  }

  // ── Reference data (GLOBAL — note the absence of customerId) ───────────────

  async listReference<T>(type: ReferenceType, includeInactive = false): Promise<Wrapped<T[]>> {
    return this.get<Wrapped<T[]>>(
      `/broadcast/reference/${type}`,
      includeInactive ? { includeInactive: 'true' } : {}
    )
  }

  async createLeague(payload: {
    code: string; name: string; shortName?: string | null; kind?: string
    sportId?: string | null; country?: string | null; displayOrder?: number
  }): Promise<Wrapped<RefLeague>> {
    return this.post<Wrapped<RefLeague>>('/broadcast/reference/leagues', payload, {})
  }

  async createTeam(payload: {
    leagueId: string; sportId?: string | null; name: string
    shortName?: string | null; abbreviation?: string | null; homeVenueId?: string | null
  }): Promise<Wrapped<RefTeam>> {
    return this.post<Wrapped<RefTeam>>('/broadcast/reference/teams', payload, {})
  }

  async createVenue(payload: {
    name: string; city?: string | null; region?: string | null
    country?: string | null; timezone?: string | null
  }): Promise<Wrapped<RefVenue>> {
    return this.post<Wrapped<RefVenue>>('/broadcast/reference/venues', payload, {})
  }

  async updateReference<T>(
    type: ReferenceType, id: string, updates: Record<string, unknown>
  ): Promise<Wrapped<T>> {
    return this.put<Wrapped<T>>(`/broadcast/reference/${type}/${id}`, updates, {})
  }
}

export const broadcastAdminService = new BroadcastAdminService()
export default broadcastAdminService

/** Support's view: any broadcast, any state. */
export const supportReviewSource = {
  getFrameUrl: (c: string, b: string, a: string) => broadcastAdminService.getFrameUrl(c, b, a),
  getPlaybackUrl: (c: string, b: string) => broadcastAdminService.getPlaybackUrl(c, b),
  getAppearances: (c: string, b: string, n: string) => broadcastAdminService.getBrandAppearances(c, b, n),
  setZone: (c: string, b: string, a: string, z: string) =>
    broadcastAdminService.setAppearanceZone(c, b, a, z),
  listZones: (broadcastId?: string) => broadcastAdminService.listZones(broadcastId),
  // Support only. mark_type is global, so the customer source deliberately
  // omits this and the control never renders for them.
  setMark: (c: string, b: string, n: string, t: MarkType | null) =>
    broadcastAdminService.setBrandMark(c, b, n, t),
  setAppearanceExclusion: (c: string, b: string, a: string, r: ExclusionReason | null) =>
    broadcastAdminService.setAppearanceExclusion(c, b, a, r),
  mergeBrand: (c: string, alias: string, into: string, name?: string) =>
    broadcastAdminService.mergeBrand(c, alias, into, name),
  unmergeBrand: (c: string, alias: string) => broadcastAdminService.unmergeBrand(c, alias)
}
